import { z } from 'zod';
import { SectionIdSchema } from './access';

/**
 * Roles & permissions (spec §4.1–4.2, §5 screen 5; D23). Roles are admin-editable bundles of
 * `area.screen.action` permissions; the catalogue is grouped by section → screen → action so
 * the editor always shows every screen that exists.
 */

export const LocationPolicySchema = z.enum(['anywhere', 'branch_network_only']);
export type LocationPolicy = z.infer<typeof LocationPolicySchema>;

export const ROLE_CODE_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

export const RoleHolderCountsSchema = z.object({
  /** Seats whose holder gets this role automatically. */
  positions: z.number().int().nonnegative(),
  /** Active explicit grants. */
  grants: z.number().int().nonnegative(),
  /** Distinct people holding it today, through either. */
  people: z.number().int().nonnegative(),
});

export const RoleSummarySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** Seeded roles can be edited but not deleted. */
  seeded: z.boolean(),
  locationPolicy: LocationPolicySchema,
  permissions: z.array(z.string()),
  holders: RoleHolderCountsSchema,
});
export type RoleSummary = z.infer<typeof RoleSummarySchema>;

export const RolesResponseSchema = z.object({ roles: z.array(RoleSummarySchema) });
export type RolesResponse = z.infer<typeof RolesResponseSchema>;

/** Who has this role, and how. */
export const RoleHolderSchema = z.object({
  personId: z.uuid(),
  name: z.string(),
  initials: z.string(),
  via: z.enum(['position', 'grant']),
  /** "Branch Manager · Stafford" or "Grant · Whole company until 31 Oct". */
  detail: z.string(),
});
export type RoleHolder = z.infer<typeof RoleHolderSchema>;

export const RoleDetailSchema = RoleSummarySchema.extend({
  holderList: z.array(RoleHolderSchema),
});
export type RoleDetail = z.infer<typeof RoleDetailSchema>;

export const PermissionDefSchema = z.object({
  code: z.string(),
  action: z.string(),
  /** Human label, e.g. "See the site". */
  name: z.string(),
});

export const PermissionScreenSchema = z.object({
  screen: z.string(),
  label: z.string(),
  actions: z.array(PermissionDefSchema),
});

export const PermissionSectionSchema = z.object({
  /** Aroo section the area belongs to; null for areas no section claims yet. */
  section: SectionIdSchema.nullable(),
  area: z.string(),
  label: z.string(),
  screens: z.array(PermissionScreenSchema),
});

export const PermissionCatalogueSchema = z.object({
  sections: z.array(PermissionSectionSchema),
});
export type PermissionCatalogue = z.infer<typeof PermissionCatalogueSchema>;
export type PermissionSection = z.infer<typeof PermissionSectionSchema>;

export const RoleCreateSchema = z.object({
  code: z.string().trim().toLowerCase().regex(ROLE_CODE_PATTERN, 'Lowercase letters, digits, _'),
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).nullable().optional(),
  locationPolicy: LocationPolicySchema.default('anywhere'),
  permissions: z.array(z.string()).max(500).default([]),
});
export type RoleCreate = z.infer<typeof RoleCreateSchema>;

export const RolePatchSchema = z.object({
  name: z.string().trim().min(1).max(100).optional(),
  description: z.string().trim().max(500).nullable().optional(),
  locationPolicy: LocationPolicySchema.optional(),
  permissions: z.array(z.string()).max(500).optional(),
});
export type RolePatch = z.infer<typeof RolePatchSchema>;

/** What a save changed, and how many people feel it. */
export const RoleDiffSchema = z.object({
  added: z.array(z.string()),
  removed: z.array(z.string()),
  fields: z.array(z.enum(['name', 'description', 'locationPolicy'])),
  peopleAffected: z.number().int().nonnegative(),
});
export type RoleDiff = z.infer<typeof RoleDiffSchema>;

export const RoleSaveResponseSchema = z.object({
  role: RoleSummarySchema,
  diff: RoleDiffSchema,
});
export type RoleSaveResponse = z.infer<typeof RoleSaveResponseSchema>;

/** 409 when a save would take `people.roles.manage` away from the person saving. */
export const SelfLockoutErrorSchema = z.object({
  error: z.literal('self_lockout'),
  message: z.string(),
});

/** 409 when deleting a role that is seeded or still in use. */
export const RoleInUseErrorSchema = z.object({
  error: z.enum(['role_seeded', 'role_in_use']),
  message: z.string(),
  holders: RoleHolderCountsSchema.optional(),
});
